/**
 * Vision attachment helpers.
 *
 * Turns a picked/dropped/pasted image into a `ChatImageAttachment` — base64
 * with no data-URI prefix, matching what `ollamaClient.ts` forwards to
 * Ollama's `/api/chat` `images` field untouched. See `VISION_SYSTEM.md`.
 */
import { createId } from "@/utils";
import type { ChatImageAttachment } from "@/types";

const MAX_DIMENSION = 1568; // Generous upper bound most vision models are tuned for.

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

/** Downscales oversized images client-side before they're base64-encoded and
 * sent — keeps the request payload and the conversation's stored
 * `images_json` reasonable for a phone photo straight off a camera. */
async function normalizeImage(blob: Blob): Promise<{ base64: string; mimeType: string }> {
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    // No canvas support (shouldn't happen in a webview) — fall back to the
    // original bytes rather than failing the attach entirely.
    const bytes = new Uint8Array(await blob.arrayBuffer());
    return { base64: bytesToBase64(bytes), mimeType: blob.type || "image/png" };
  }
  ctx.drawImage(bitmap, 0, 0, width, height);

  const mimeType = "image/jpeg";
  const dataUrl = canvas.toDataURL(mimeType, 0.9);
  return { base64: dataUrl.slice(dataUrl.indexOf(",") + 1), mimeType };
}

export async function fileToAttachment(file: File): Promise<ChatImageAttachment> {
  const { base64, mimeType } = await normalizeImage(file);
  return { id: createId("img"), base64, mimeType };
}

export async function bytesToAttachment(bytes: Uint8Array, mimeType: string): Promise<ChatImageAttachment> {
  // Cast needed because `Uint8Array`'s generic `ArrayBufferLike` backing
  // (which includes `SharedArrayBuffer`) is narrower than `BlobPart` allows
  // as of the current DOM lib types; the bytes themselves are always a
  // plain, non-shared buffer here.
  const blob = new Blob([bytes as unknown as BlobPart], { type: mimeType });
  return fileToAttachment(new File([blob], "attachment", { type: mimeType }));
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}
